import React from 'react';
import { Product } from 'src/utils/helpers/product';
import { ApolloError } from '@apollo/client';

import { ViewportVisibility } from 'src/hooks/use-viewport-visibility';
import { ConsumerOrder } from 'src/components/embedded-account/account.types';

import type { Theme } from '../../../types';
import { useTabNav } from '../../utils/use-tab-nav';

import { BuyItAgainList } from './buy-it-again-list';
import { BuyItAgainTabs, TabValue } from './buy-it-again-tabs';
import { ItemsTab } from './items-tab/items-tab';
import { OrdersTab } from './orders-tab/orders-tab';

type BuyItAgainProps = {
  error?: ApolloError;
  handleProductClick: (product: Product, index: number) => void;
  handleQuickAddToCart: (product: Product, index: number) => void;
  handleViewAllClick: (destinationUrl: string) => void;
  products: Product[];
  theme: Theme;
  viewportVisibility: ViewportVisibility;
  orders?: ConsumerOrder[];
  reorder?: (order: ConsumerOrder) => Promise<void>;
  loadingOrderId?: string | null;
  handleViewOrder?: (order: ConsumerOrder) => void;
};

export const BuyItAgain = ({
  error,
  handleProductClick,
  handleQuickAddToCart,
  handleViewAllClick,
  products,
  theme,
  viewportVisibility,
  orders = [],
  reorder,
  loadingOrderId = null,
  handleViewOrder,
}: BuyItAgainProps): JSX.Element | null => {
  const { backgroundColor } = theme;
  const { ref } = viewportVisibility;

  const showOrdersTab = reorder !== undefined && orders.length > 0;
  const [activeTab, setActiveTab] = useTabNav<TabValue>('items', showOrdersTab, 'orders');

  if (error || !products.length) {
    return null;
  }

  const tabControls = showOrdersTab ? <BuyItAgainTabs activeTab={activeTab} onTabChange={setActiveTab} /> : undefined;

  return (
    <BuyItAgainList handleViewAllClick={handleViewAllClick} ref={ref} tabControls={tabControls}>
      {activeTab === 'orders' && showOrdersTab && handleViewOrder ? (
        <OrdersTab
          orders={orders}
          reorder={reorder}
          loadingOrderId={loadingOrderId}
          handleViewOrder={handleViewOrder}
        />
      ) : (
        <ItemsTab
          backgroundColor={backgroundColor}
          products={products}
          handleProductClick={handleProductClick}
          handleQuickAddToCart={handleQuickAddToCart}
        />
      )}
    </BuyItAgainList>
  );
};
